import { useEffect, useState } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import {
  Box,
  Typography,
  Stack,
  MenuItem,
  Avatar,
  IconButton,
  Drawer,
  ListItemIcon,
  ListItemText,
  Paper,
  useTheme,
} from '@mui/material';
import { Home as HomeIcon, Person as PersonIcon, Logout as LogoutIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getProfile } from '../../../shared/auth/authService';
import { logoutUser } from '../../../packages/auth/store/authActions';

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState({});
  const navigate = useNavigate();
  const theme = useTheme();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleNavigation = (path) => {
    handleClose();
    navigate(path);
  };

  const handleLogout = () => {
    handleClose();
    dispatch(logoutUser());
  };

  useEffect(() => {
    const profile = getProfile();
    if (profile) setUser(profile.user);
  }, []);

  const MENU_OPTIONS = [
    {
      label: 'Home',
      icon: <HomeIcon />,
      onClick: () => handleNavigation('/dashboard/app'),
    },
    {
      label: 'Profile',
      icon: <PersonIcon />,
      onClick: () => handleNavigation('/dashboard/settings'),
    },
  ];

  const displayName = user?.display_name || user?.name || 'User';
  const displayContact = user?.email || user?.mobile_number || '';

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          p: 0,
          position: 'relative',
          borderRadius: '50%',
          transition: 'all 0.2s ease-in-out',
          ...(open && {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.1),
            },
          }),
        }}
        aria-label="account menu"
        aria-controls={open ? 'account-drawer' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar
          src={user?.photoURL}
          alt={displayName}
          sx={{
            width: 36,
            height: 36,
            bgcolor: (theme) => theme.palette.primary.main,
            fontSize: 16,
            fontWeight: 600,
            color: 'white',
          }}
        >
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        id="account-drawer"
        PaperProps={{
          sx: {
            width: 320,
            maxWidth: '85vw',
            bgcolor: 'background.paper',
            borderRadius: theme.spacing(0, 0, 0, 2),
            boxShadow: theme.shadows[16],
          },
        }}
        SlideProps={{
          timeout: 300,
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              pb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {''}
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* User Info Section */}
          <Paper
            elevation={0}
            sx={{
              m: 3,
              mt: 2,
              p: 3,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={user?.photoURL}
                alt={displayName}
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: (theme) => theme.palette.primary.main,
                  fontSize: 20,
                  fontWeight: 600,
                  color: 'white',
                }}
              >
                {displayName.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    color: 'text.primary',
                  }}
                  noWrap
                >
                  {displayName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                  }}
                  noWrap
                >
                  {displayContact}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Menu Options */}
          <Box sx={{ flex: 1, px: 3, pb: 2 }}>
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: 1.2,
                mb: 1,
                px: 1,
              }}
            >
              Navigation
            </Typography>
            <Stack spacing={0.5}>
              {MENU_OPTIONS.map((option) => (
                <MenuItem
                  key={option.label}
                  onClick={option.onClick}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    px: 2,
                    minHeight: 56,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 44,
                      color: 'text.primary',
                      '& svg': {
                        fontSize: 22,
                      },
                    }}
                  >
                    {option.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{
                      variant: 'body1',
                      fontWeight: 500,
                    }}
                  />
                </MenuItem>
              ))}
            </Stack>
          </Box>

          {/* Logout Section */}
          <Box
            sx={{
              p: 3,
              pt: 2,
              borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
              mt: 'auto',
            }}
          >
            <MenuItem
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                py: 1.5,
                px: 2,
                color: 'error.main',
                minHeight: 56,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                  transform: 'translateX(4px)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 44,
                  color: 'error.main',
                  '& svg': {
                    fontSize: 22,
                  },
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  variant: 'body1',
                  fontWeight: 500,
                }}
              />
            </MenuItem>
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
